import { useEffect, useRef } from 'react';
import type { ComposerSubmitComment } from '@liveblocks/react-ui/primitives';
import { Composer } from '@liveblocks/react-ui/primitives';
import { RoomProvider } from '@liveblocks/react';
import type { RoomType } from 'providers/RoomRegistrar';
import '@liveblocks/react-ui/styles.css';
import {
  PySubmitButton,
  PyCancelButton,
  PyThreadInputActions,
  PyThreadEditor,
  PyComposerMention,
  PyComposerSuggestionsList,
  PyComposerSuggestions,
  PyComposerSuggestionsListItem,
} from './styled';
import type { CommentBody } from '@liveblocks/client';
import { useMergedThreads } from 'providers/MergedThreadsProvider';
import { extractNameFromMentionId } from './utils/mentionUtils';

interface CustomComposerProps {
  roomType: RoomType;
  onCancel: () => void;
  onSubmit: (comment: ComposerSubmitComment) => Promise<void>;
  placeholder?: string;
  submitLabel?: string;
  cancelLabel?: string;
  defaultValue?: CommentBody;
}

const MentionSuggestions = ({ mentions }: { mentions: Array<{ id: string }> }) => (
  <PyComposerSuggestions>
    <PyComposerSuggestionsList>
      {mentions.map((mention) => {
        const displayName = extractNameFromMentionId(mention.id);
        return (
          <PyComposerSuggestionsListItem key={mention.id} value={mention.id}>
            {displayName}
          </PyComposerSuggestionsListItem>
        );
      })}
    </PyComposerSuggestionsList>
  </PyComposerSuggestions>
);

export const CustomComposer = ({
  roomType,
  onCancel,
  onSubmit,
  placeholder,
  submitLabel,
  cancelLabel,
  defaultValue,
}: CustomComposerProps) => {
  const { contentId } = useMergedThreads();
  const roomId = `content:${contentId}:${roomType}`;
  const containerRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const contentEditable = containerRef.current?.querySelector('[contenteditable="true"]') as HTMLElement;
    if (contentEditable && contentEditable !== document.activeElement) {
      contentEditable.click();
      contentEditable.focus();
    }
  }, []);

  return (
    <RoomProvider id={roomId}>
      <Composer.Form onComposerSubmit={onSubmit} ref={containerRef}>
        <PyThreadEditor
          placeholder={placeholder}
          defaultValue={defaultValue}
          components={{
            Mention: PyComposerMention,
            MentionSuggestions,
          }}
        />

        <PyThreadInputActions>
          <PyCancelButton type="button" onClick={onCancel}>
            {cancelLabel}
          </PyCancelButton>
          <PySubmitButton>{submitLabel}</PySubmitButton>
        </PyThreadInputActions>
      </Composer.Form>
    </RoomProvider>
  );
};
